// B750 (DT-57, Q-NAV-19, ledger L823): legacy's API layer returns a 403 or 404 through its success path, the store
// then throws a TypeError nobody shows, and the page silently does nothing. Here a read that fails while its page
// already shows data says so in an error toast and the page keeps what it showed; a read that fails before any data
// shows the page's load error instead (Q-GLB-01, `LoadError`), so pages branch on `isLoadingError`, not `isError`.
import { useEffect, useRef } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { useToast } from '@radish/ui-web';
import { CallFailure } from '../data/api.tsx';

/** One toast per message in this window: a command that invalidates several reads that all fail says it once. */
const REPEAT_WINDOW_MS = 2_000;

export function RefetchErrorToasts({ queryClient }: { readonly queryClient: QueryClient }): null {
  const toast = useToast();
  const lastShown = useRef(new Map<string, number>());
  useEffect(() => queryClient.getQueryCache().subscribe((event) => {
    if (event.type !== 'updated' || event.action.type !== 'error') return;
    const { error } = event.action;
    if (event.query.state.data === undefined || !(error instanceof CallFailure) || event.query.meta?.['ownErrorToast'] === true) return;
    // An expired session signs out on its own (unwrap's signOut('expired')): the sign-in page says why, no toast.
    if (error.code === 'unauthenticated') return;
    const now = Date.now();
    const shown = lastShown.current.get(error.message);
    if (shown !== undefined && now - shown < REPEAT_WINDOW_MS) return;
    lastShown.current.set(error.message, now);
    toast.error(error.message);
  }), [queryClient, toast]);
  return null;
}
